import { useReducer } from "react";
import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import { useDeviceCommand } from "src/providers/DeviceCommandProvider";
import { makeStyles } from "tss-react/mui";

import { NewCommandDialog } from "@dashboard/devices/components/Commands/NewCommandDialog";
import { Device } from "@dashboard/devices/types";
import { MdmCommand } from "@dashboard/devices/types/commands";
import { Box, TableRow } from "@mui/material";
import { SecondaryButton } from "@shared/CustomButton";
import { MinimalTable, MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { Header } from "@shared/table/TableHeader";
import CustomTablePagination from "@shared/table/TablePagination";
import { createColumnHelper, getCoreRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";

import { PaneElement } from "../../shared/PaneElement";

import DeviceCommandItem from "./DeviceCommandItem";

const useStyles = makeStyles()((theme) => ({
    tooltipWrapper: {
        display: "block",
        height: "100%",
    },
    headerBox: { display: "flex", justifyContent: "space-between", paddingBottom: theme.spacing(1) },
    btnBox: { display: "flex", gap: theme.spacing(1) },
}));

const columnHelper = createColumnHelper<MdmCommand>();

const columns = [
    columnHelper.accessor("id", {
        id: "collapse",
        header: "",
        cell: () => null,
        enableSorting: false,
    }),
    columnHelper.accessor("created", {
        id: "date",
        header: i18next.t("sidepanels:devicePanel.table.header.date"),
        cell: (info) => info.getValue(),
        enableSorting: true,
    }),
    columnHelper.accessor("status", {
        id: "status",
        header: i18next.t("common:label.status"),
        cell: (info) => info.getValue(),
        meta: { align: "center" },
        enableSorting: true,
    }),
    columnHelper.accessor("origin", {
        id: "origin",
        header: i18next.t("sidepanels:devicePanel.table.header.origin"),
        cell: (info) => info.getValue(),
        meta: { align: "center" },
        enableSorting: true,
    }),
    columnHelper.accessor("payload", {
        id: "command",
        header: i18next.t("sidepanels:devicePanel.table.header.command"),
        cell: (info) => info.getValue(),
        enableSorting: true,
    }),
    columnHelper.accessor("id", {
        id: "actions",
        header: "",
        cell: () => null,
        enableSorting: false,
    }),
];

const CommandsTab = ({ device }: { device: Device }) => {
    const [dialogOpen, toggleDialog] = useReducer((state) => !state, false);
    const { classes } = useStyles();
    const { t } = useTranslation(["sidepanels", "modals"]);

    const commandsContext = useDeviceCommand();

    const table = useReactTable({
        data: commandsContext.data,
        columns,
        state: {
            sorting: commandsContext.sorting,
            pagination: {
                pageIndex: commandsContext.pageIndex,
                pageSize: commandsContext.pageSize,
            },
        },
        onSortingChange: commandsContext.setSorting,
        pageCount: Math.ceil(commandsContext.totalCount / commandsContext.pageSize),
        manualPagination: true,
        manualSorting: true,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const handlePageChange = (_: unknown, newPage: number) => {
        commandsContext.setPageIndex(newPage);
    };

    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newPageSize = parseInt(event.target.value, 10);
        commandsContext.setPageSize(newPageSize);
        commandsContext.setPageIndex(0);
    };

    return (
        <PaneElement.Container>
            <Box className={classes.headerBox}>
                <PaneElement.Header>{t("sidepanels:devicePanel.commandsTab.lastCommands")}</PaneElement.Header>
                <SecondaryButton
                    onClick={toggleDialog}
                    tooltipProps={{ title: t("sidepanels:devicePanel.tooltip.createCommand") }}
                >
                    <i className="fas fa-solid fa-square-plus" />
                </SecondaryButton>
            </Box>

            <PaneElement.TableLayout
                contentDeps={[commandsContext.pageIndex, commandsContext.pageSize, commandsContext.data.length]}
                pagination={
                    <CustomTablePagination
                        component="div"
                        count={commandsContext.totalCount}
                        rowsPerPageOptions={commandsContext.rowsPerPage}
                        rowsPerPage={commandsContext.pageSize}
                        page={commandsContext.pageIndex}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        showFirstButton
                        showLastButton
                    />
                }
            >
                <MinimalTable>
                    <MinimalTableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <Header key={header.id} {...header} />
                                ))}
                            </TableRow>
                        ))}
                    </MinimalTableHeader>
                    <TableBody
                        table={table}
                        loading={commandsContext.isLoading}
                        noDataMessage={t("sidepanels:devicePanel.table.noCommands")}
                    >
                        {table.getRowModel().rows.map((row, index) => (
                            <DeviceCommandItem
                                key={row.original.id}
                                index={index}
                                command={row.original}
                                isDeleting={commandsContext.deletingId === row.original.id}
                                isCreating={commandsContext.resendingId === row.original.id}
                                createCommand={commandsContext.onCreate}
                                deleteCommand={commandsContext.onDelete}
                            />
                        ))}
                    </TableBody>
                </MinimalTable>
            </PaneElement.TableLayout>
            <NewCommandDialog
                title={t("modals:newCommand.title")}
                device={device}
                isOpen={dialogOpen}
                toggleDialog={toggleDialog}
                createCommand={commandsContext.onCreate}
            />
        </PaneElement.Container>
    );
};

export default CommandsTab;
